/**
 * Pure helpers for URL Management V2 (Basic). See ./URL_MANAGEMENT_V2.md and project skill `.cursor/skills/url-management-v2/`.
 */

export const URL_PREDEFINED_FULL_PATTERNS = [
  '/:title',
  '/:year/:month/:day/:title',
  '/:year/:month/:title'
] as const

export const URL_PREDEFINED_PATTERN_LABELS: Record<string, string> = {
  '/:title': 'Title',
  '/:year/:month/:day/:title': 'Date and Title',
  '/:year/:month/:title': 'Month and Title'
}

export const isUrlPredefinedFullPattern = (pattern: string | false | undefined | null): boolean => {
  if (pattern == null || pattern === false) return false
  const s = String(pattern)
  if (!s) return false
  return (URL_PREDEFINED_FULL_PATTERNS as readonly string[]).includes(s)
}

export type TaxonomyUidOption = { taxonomy_uid: string }

/**
 * Collects `taxonomy_uid` values from every root-level field with `data_type === 'taxonomy'`
 * and a `taxonomies` array. De-duplicated.
 */
export const getTaxonomyUidOptionsFromSchema = (schemaTree: unknown): TaxonomyUidOption[] => {
  if (!Array.isArray(schemaTree)) return []
  const seen = new Set<string>()
  const out: TaxonomyUidOption[] = []
  for (const field of schemaTree) {
    if (field?.data_type !== 'taxonomy' || !Array.isArray(field.taxonomies)) continue
    for (const t of field.taxonomies) {
      const uid = t?.taxonomy_uid
      if (typeof uid === 'string' && uid.length && !seen.has(uid)) {
        seen.add(uid)
        out.push({ taxonomy_uid: uid })
      }
    }
  }
  return out
}

/** True if the root schema includes at least one taxonomy field (show Taxonomy in the pattern menu). */
export const schemaHasTaxonomyField = (schemaTree: unknown): boolean =>
  Array.isArray(schemaTree) && schemaTree.some((f: any) => f?.data_type === 'taxonomy')

/** Icon names for `Icon` v2 rows in URL pattern suggestion menus (Figma). */
export type UrlPatternSuggestionRowIcon = 'EntriesSecondary' | 'Taxonomy' | 'CalendarBlank' | 'Language'

const URL_PATTERN_INSERT_ICONS_ENTRIES: ReadonlySet<string> = new Set([
  ':title',
  ':unique_id',
])

/** Syntax menu insert tokens (lowercase) — used to distinguish partial type-ahead from a complete token. */
const URL_PATTERN_COMPLETE_SYNTAX_TOKENS: ReadonlySet<string> = new Set([
  ':title',
  ':unique_id',
  ':locale',
  ':locale_name',
  ':year',
  ':year_short',
  ':month',
  ':monthname',
  ':monthname_short',
  ':day',
  ':year-:month-:day'
])

export const isCompleteUrlPatternSyntaxToken = (token: string): boolean =>
  URL_PATTERN_COMPLETE_SYNTAX_TOKENS.has(String(token).toLowerCase())

/**
 * When the value already ends with a complete token and the pick is a longer token that
 * shares that prefix (e.g. `:locale` then `:locale_name`), append instead of replacing.
 */
const shouldAppendAfterCompleteToken = (existingToken: string, insert: string): boolean => {
  const existing = existingToken.toLowerCase()
  const ins = insert.toLowerCase()
  return isCompleteUrlPatternSyntaxToken(existing) && ins !== existing && ins.startsWith(existing)
}

/** Syntax token row: entry-style tokens vs date-style vs (taxonomy is never a token row). */
export const getUrlPatternSyntaxTokenIcon = (insert: string): UrlPatternSuggestionRowIcon => {
  if (URL_PATTERN_INSERT_ICONS_ENTRIES.has(insert)) return 'EntriesSecondary'
  if (insert === ':locale' || insert === ':locale_name') return 'Language'
  return 'CalendarBlank'
}

/** Case-insensitive substring match for suggestion filtering (empty query shows all). */
export const urlPatternSuggestionMatches = (query: string, ...haystacks: (string | undefined | null)[]): boolean => {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return haystacks.some((h) => h != null && String(h).toLowerCase().includes(q))
}

/** Trailing URL / pattern punctuation — not used for narrowing the suggestion list. */
const SUGGESTION_QUERY_TRAILING_STRUCTURE = /[\/\-_.:\s]+$/u

/**
 * Longest suffix of the pattern that is only `:`, letters, digits, or `_` (the fragment the user is
 * currently typing, e.g. `t` after `...-day-`, or `:mo` for `:month`).
 */
const SUGGESTION_TAIL_FRAGMENT = /([:a-zA-Z0-9_]+)$/u

/**
 * Longest suffix of `s` made only of `:`, ASCII letters, digits, and `_` (same tail used for type-ahead filtering).
 * Empty string when the pattern ends with a separator or there is no such run (e.g. `/:title-` → `''`).
 */
export const getUrlPatternTailFragmentAtEnd = (s: string): string => {
  const m = String(s).match(SUGGESTION_TAIL_FRAGMENT)
  return m ? m[1] : ''
}

/**
 * Query string passed to `urlPatternSuggestionMatches` while the user is typing.
 *
 * - When **filtering is inactive** (menu opened from a click without typing yet), returns `''` → full list.
 * - When the value ends with only **structural** characters (`/`, `-`, `_`, `.`, `:`, spaces), returns `''` so
 *   editors can place separators between tokens (e.g. `/:title-` → still show every suggestion; no “no matches”
 *   row for a lone `-`).
 * - If the pattern has **no** letter or digit yet (only punctuation), returns `''`.
 * - Otherwise returns the **tail fragment** after the last separator run (letters/digits/`:`/`_` only), so long
 *   patterns like `/:title:...:day-t` filter by `t` / `title` / `:mo`, not the entire string (which would match nothing).
 */
export const getUrlPatternSuggestionFilterQuery = (raw: string, filterActive: boolean): string => {
  if (!filterActive) return ''
  const t = String(raw).trimEnd()
  if (!t) return ''
  if (!/[a-zA-Z0-9]/.test(t)) {
    return ''
  }
  const withoutTrailing = t.replace(SUGGESTION_QUERY_TRAILING_STRUCTURE, '')
  if (withoutTrailing.length < t.length) {
    return ''
  }
  const m = t.match(SUGGESTION_TAIL_FRAGMENT)
  return m ? m[1] : ''
}

export type ApplyUrlPatternSuggestionResult = { value: string; caret: number }

/**
 * Apply a picked suggestion: replace selected range, or replace trailing path segment when it
 * looks like a partial type-ahead (e.g. `loc` → `:locale_code`), else insert at caret.
 * Returns new value and caret position after the inserted token.
 */
export const applyUrlPatternSuggestion = (
  patternValue: string,
  insert: string,
  suggestionLabel: string,
  selectionStart: number | null | undefined,
  selectionEnd: number | null | undefined
): ApplyUrlPatternSuggestionResult => {
  const len = patternValue.length
  const start = Math.min(Math.max(0, selectionStart ?? len), len)
  const end = Math.min(Math.max(0, selectionEnd ?? len), len)

  if (start !== end) {
    const value = patternValue.slice(0, start) + insert + patternValue.slice(end)
    return { value, caret: start + insert.length }
  }

  const beforeCaret = patternValue.slice(0, start)
  const afterCaret = patternValue.slice(end)
  const insLower = insert.toLowerCase()
  const labLower = (suggestionLabel || '').toLowerCase()
  const insertBare = insLower.replace(/^:/, '')

  /** True when `tail` is the fragment the user was typing to narrow the list for this suggestion. */
  const tailMatchesSuggestion = (tail: string): boolean => {
    if (!tail) return false
    const tl = tail.toLowerCase()
    if (tl === insLower || tl === labLower || tl === insertBare) return true
    if (tl.startsWith(':') && insLower.startsWith(tl)) {
      return !shouldAppendAfterCompleteToken(tl, insert)
    }
    if (tl.length >= 2) {
      return (
        labLower.startsWith(tl) ||
        insertBare.startsWith(tl) ||
        labLower.includes(tl) ||
        insertBare.includes(tl)
      )
    }
    return labLower.startsWith(tl) || insertBare.startsWith(tl)
  }

  const tail = getUrlPatternTailFragmentAtEnd(beforeCaret)
  if (tailMatchesSuggestion(tail)) {
    const newBefore = beforeCaret.slice(0, beforeCaret.length - tail.length)
    const value = newBefore + insert + afterCaret
    return { value, caret: newBefore.length + insert.length }
  }

  const lastSlash = beforeCaret.lastIndexOf('/')
  const prefix = lastSlash >= 0 ? beforeCaret.slice(0, lastSlash + 1) : ''
  const segment = lastSlash >= 0 ? beforeCaret.slice(lastSlash + 1) : beforeCaret
  const segLower = segment.toLowerCase()

  const shouldReplaceSegment =
    segment.length > 0 &&
    !shouldAppendAfterCompleteToken(segment, insert) &&
    (insLower.includes(segLower) ||
      labLower.includes(segLower) ||
      insertBare.includes(segLower) ||
      labLower.startsWith(segLower))

  if (shouldReplaceSegment) {
    const value = prefix + insert + afterCaret
    return { value, caret: prefix.length + insert.length }
  }

  const value = beforeCaret + insert + afterCaret
  return { value, caret: start + insert.length }
}

/**
 * Root-level text fields for `:field[field_uid]`.
 * Excludes title, url, multi-value text (`multiple === true`), rich text (`field_metadata.allow_rich_text`),
 * and markdown (`field_metadata.markdown`).
 */
export const getRootTextFieldsForUrlPattern = (schemaTree: unknown): Array<{ uid: string; display_name: string }> => {
  if (!Array.isArray(schemaTree)) return []
  return schemaTree
    .filter((field: any) => {
      if (!field?.uid || field.uid === 'title' || field.uid === 'url') return false
      if (field.data_type !== 'text') return false
      if (!(field.GroupnestingDepth === 0 || field.GroupnestingDepth === undefined)) return false
      if (!(field.MBnestingDepth === 0 || field.MBnestingDepth === undefined)) return false
      if (field.empty) return false
      if (field.multiple === true) return false
      const fm = field.field_metadata
      if (fm?.allow_rich_text) return false
      if (fm?.markdown) return false
      return true
    })
    .map((f: any) => ({
      uid: f.uid,
      display_name: f.display_name || f.uid
    }))
}

/** Insert string for a selected text field UID */
export const fieldPatternToken = (fieldUid: string): string => `:field[${fieldUid}]`

/** Insert string for a selected taxonomy UID */
export const taxonomyPatternToken = (taxonomyUid: string): string => `:taxonomy[${taxonomyUid}]`

/**
 * Value for the URL **pattern** editor only (`options.url_pattern`).
 * `url_prefix` is edited separately (same as legacy Basic URL UI).
 */
export const getUrlPatternFieldValueFromOptions = (options: { url_pattern?: string | false }): string => {
  if (options?.url_pattern === false) return ''
  return options?.url_pattern ? String(options.url_pattern) : ''
}
